import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAccount } from "../../app/account";
import { isEmail } from "../../core/account/credentials";
import { safeNext } from "../../core/account/safeNext";
import { accountErrorKind, AccountErrorKind } from "../../core/account/types";
import { buttonClass, LoadingScreen } from "../../components/StatusScreen";
import { en } from "../../i18n/en";
import { saveReturnPath } from "../../platform/web/returnPath";
import { AccountPage, Field, FormError, GoogleButton } from "./AccountPage";

const t = en.account;

/** The landing page for signed-out visitors (specs/11 FR-58, FR-59; specs/12 FR-81, FR-83). */
export function SignInScreen() {
  const { state, services, linkError, clearLinkError } = useAccount();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [error, setError] = useState<AccountErrorKind | null>(null);
  const [busy, setBusy] = useState(false);

  // Going back from Google's page can restore this page from the browser's back/forward cache
  // exactly as it was left, still busy. Showing it again means sign-in didn't happen.
  useEffect(() => {
    const reset = () => setBusy(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  if (state.status === "checking") return <LoadingScreen />;
  // Signed in, by this form or already: go where they were headed.
  if (state.status === "signed-in") return <Navigate to={next} replace />;

  const shownError = error ?? linkError;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    clearLinkError();
    setError(null);
    const valid = isEmail(email);
    setEmailError(!valid);
    if (!valid || !password) return;
    setBusy(true);
    try {
      await services.account.signIn(email.trim(), password);
    } catch (err) {
      setError(accountErrorKind(err));
      setBusy(false);
    }
  };

  const google = async () => {
    clearLinkError();
    setError(null);
    setBusy(true);
    saveReturnPath(next);
    try {
      await services.account.signInWithGoogle();
    } catch (err) {
      setError(accountErrorKind(err));
      setBusy(false);
    }
  };

  return (
    <AccountPage title={t.signIn.title}>
      <p className="mb-1 text-slate-300">{t.tagline}</p>
      <p className="mb-4 font-medium text-slate-100">{t.inviteOnly}</p>
      <div className="flex flex-col gap-4">
        {shownError && <FormError>{t.errors[shownError]}</FormError>}
        <GoogleButton onClick={google} disabled={busy} />
        <p className="flex items-center gap-3 text-xs uppercase text-slate-500" aria-hidden="true">
          <span className="h-px flex-1 bg-slate-700" />
          {t.signIn.or}
          <span className="h-px flex-1 bg-slate-700" />
        </p>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <Field
            label={t.signIn.email}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError ? t.emailInvalid : null}
          />
          <Field
            label={t.signIn.password}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" disabled={busy} className={buttonClass}>
            {busy ? t.signIn.working : t.signIn.submit}
          </button>
        </form>
        <Link to="/reset-password" className="text-center text-sm text-sky-400 underline">
          {t.signIn.forgot}
        </Link>
      </div>
    </AccountPage>
  );
}
